import { useEffect } from 'react'

import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { Button, Label } from '@/components/ui'
import { CheckboxGroup, CheckboxGroupItem } from '@/components/ui/checkbox-group'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeaderContent,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/brasil/components/layouts'
import { obterElegiveisParaProcurador } from '@/features/proposta/brasil/domain/Comprador/opcoes-comprador-menu'
import type { TProcuradorPropostaSchema } from '@/features/proposta/brasil/domain/schemas'
import type {
  CompradorVinculoStepSchemaInput,
  CompradorVinculoStepSchemaOutput,
} from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import { Cnpj } from '@/utils/cnpj'
import { Cpf } from '@/utils/cpf'

type TSelecaoCompradorMenorVinculo = {
  compradores: TCompradoresCadastrados
  procuradores: TProcuradorPropostaSchema[]
  onBack: () => void
  onFinish: (value: CompradorVinculoStepSchemaOutput) => Promise<void>
}

export const VincularCompradorAoProcurador = ({
  compradores,
  onBack,
  onFinish,
  procuradores,
}: TSelecaoCompradorMenorVinculo) => {
  const {
    trigger,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useFormContext<CompradorVinculoStepSchemaInput, unknown, CompradorVinculoStepSchemaOutput>()
  const procuradorId = watch('dados.id')
  const compradorVinculosIds = watch('compradorVinculadoIds')
  const isEditing = watch('mode') === 'edit'

  const opcoes = obterElegiveisParaProcurador(compradores, procuradores, procuradorId)

  const handleValidateOnNext = async () => {
    const isValid = await trigger('compradorVinculadoIds')

    if (isValid) {
      handleSubmit(onFinish)()
    }
  }

  const tipoComprador = (tipo: (typeof opcoes)[0]['origem']) => {
    if (tipo === 'Comprador') return 'Comprador'
    if (tipo === 'MaePai') return 'Mãe/Pai'
    return 'Representante'
  }

  const isPJComUnicoVinculoObrigatorio = (opcaoId: string) => {
    const opcao = opcoes.find((elegivel) => elegivel.id === opcaoId)
    if (opcao?.tipo !== 'PessoaJuridica') return false

    const compradorPJ = compradores.find((comprador) => comprador?.id === opcaoId)
    if (!compradorPJ) return false

    const vinculosDoCompradorPJ = procuradores.filter((procurador) => procurador.clientesIds.includes(opcaoId))
    return (
      vinculosDoCompradorPJ.length === 1 &&
      vinculosDoCompradorPJ[0]?.procuradorId === procuradorId &&
      compradorPJ.vinculos.length === 0
    )
  }

  const handleTodosCompradoresVinculados = () => {
    const todosVinculados = opcoes.filter((opcao) => opcao.jaEstaVinculado).map((opcao) => opcao.id)
    setValue('compradorVinculadoIds', todosVinculados)
  }
  useEffect(() => {
    if (compradorVinculosIds.length === 0) {
      handleTodosCompradoresVinculados()
    }
  }, [])

  return (
    <StepPropostaRoot>
      <StepPropostaHeaderContent>
        <StepPropostaHeaderTitle>
          {t('proposta:cadastro_compradores.steps.vincular_ao_procurador.titulo')}
        </StepPropostaHeaderTitle>
      </StepPropostaHeaderContent>
      <StepPropostaContent hasBorder className="pb-2">
        <div className="grid pt-3">
          <CheckboxGroup
            className="flex flex-col gap-6"
            value={compradorVinculosIds}
            onValueChange={(value) => {
              setValue('compradorVinculadoIds', value, { shouldValidate: true })
            }}
          >
            {opcoes.map((option) => (
              <div key={option.documento} className="flex items-center gap-2">
                <CheckboxGroupItem
                  value={option.id}
                  id={option.documento}
                  checked={option.jaEstaVinculado && !isEditing ? true : compradorVinculosIds.includes(option.id)}
                  disabled={(option.jaEstaVinculado && !isEditing) || isPJComUnicoVinculoObrigatorio(option.id)}
                />
                <Label className="font-nunito grid font-semibold" htmlFor={option.documento}>
                  <p className="font-semibold">{tipoComprador(option.origem)}</p>
                  <p className="font-normal">{option.nome}</p>
                  <span className="font-normal">
                    {option.tipo === 'PessoaJuridica'
                      ? t('proposta:cadastro_compradores.steps.vincular_ao_procurador.CNPJ', {
                          cnpj: new Cnpj(option.documento).format(),
                        })
                      : t('proposta:cadastro_compradores.steps.vincular_ao_procurador.CPF', {
                          cpf: new Cpf(option.documento).format(),
                        })}
                  </span>
                </Label>
              </div>
            ))}
          </CheckboxGroup>
          <span className="text-destructive mt-3 flex h-6 items-center text-xs">
            {errors.compradorVinculadoIds?.message}
          </span>
        </div>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button className="font-nunito h-12 w-full md:w-auto" variant="outline" onClick={onBack}>
          {t('proposta:cadastro_compradores.steps.voltar')}
        </Button>
        <Button
          className="font-nunito h-12 w-full md:w-27"
          isLoading={isSubmitting}
          showLoadingText={false}
          onClick={handleValidateOnNext}
        >
          {t('proposta:cadastro_compradores.steps.concluir')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
